import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { CampaignSearch } from './search';
import { pageParamKey, sizeParamKey, type PageNamespace } from './pageParamKeys';

/** Rows-per-page options offered by the roster tables' size dropdown.
 *  10/25/50/100 is the conventional set (Material / MUI DataGrid / AG Grid);
 *  the paginator also appends the row count itself as a "show all" max. */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/** Default rows per page. */
export const DEFAULT_PAGE_SIZE = 25;

/** Hard cap on a hand-editable `?size=` value for endpoints without a tighter limit. */
const MAX_PAGE_SIZE = 10000;

/** Go's MaxOutreachPageLimit (internal/outreach/views.go): a larger ?size=
 *  would be clamped server-side and the footer would describe the wrong page
 *  (E2E O1). Shared with useCampaignDeepLinkPanel, which re-resolves a
 *  deep-linked creator's page at this size when the resolver lands on
 *  Outreach and the caller's own page size exceeds it. */
export const OUTREACH_MAX_PAGE_SIZE = 100;

/** Coerce a raw `?size=` value into a usable page size: any positive integer
 *  within the backend's bounds (the dropdown can offer the row count itself),
 *  else the default. */
export function normalizePageSize(raw: number | undefined, max: number): number {
  if (!raw || !Number.isInteger(raw) || raw < 1) return DEFAULT_PAGE_SIZE;
  return Math.min(raw, max);
}

/** The `?size=` value to write: omitted when it equals the default so ordinary
 *  links stay clean and only a deliberately-changed size is encoded. */
function sizeParam(next: number): number | undefined {
  if (next === DEFAULT_PAGE_SIZE) return undefined;
  return next;
}

/**
 * URL-owned page size (`?size=`, or `${namespace}_size`), mirroring useCampaignPageParam. An
 * unrecognised value falls back to the default rather than requesting an
 * arbitrary limit.
 */
export function useCampaignPageSizeParam(
  maxPageSize: number = MAX_PAGE_SIZE,
  namespace?: PageNamespace,
): [number, (n: number) => void] {
  const navigate = useNavigate();
  const sizeKey = sizeParamKey(namespace);
  const pageKey = pageParamKey(namespace);
  const search = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const current = normalizePageSize(search[sizeKey], maxPageSize);

  const setSize = useCallback(
    (next: number) =>
      navigate({
        to: '.',
        // Changing page size invalidates the current offset — a user on page 4
        // at 10/page is not on page 4 at 25/page — so reset to page 1.
        search: ((p: Partial<CampaignSearch>) => ({
          ...p,
          [sizeKey]: sizeParam(next),
          [pageKey]: undefined,
        })),
        // In-place resize of the current view — don't let scrollRestoration
        // reset the window to the top (see useCampaignPageParam).
        resetScroll: false,
      } as never),
    [navigate, sizeKey, pageKey],
  );

  return [current, setSize];
}
